import React, { useState, useEffect, useRef } from 'react';
import { AppItem, AppLanguage, PortfolioText } from '../types';
import { getScreenshotSrc } from '../utils/screenshots';
import { 
  ChevronLeft, ChevronRight, Hand, Pause, Play,
  Image as ImageIcon, Eye, Smartphone, Code, Globe 
} from 'lucide-react';

interface AppCarouselProps {
  apps: AppItem[];
  language: AppLanguage;
  text: PortfolioText;
  autoPlayPaused: boolean;
  onSelect: (app: AppItem) => void;
}

const formatText = (template: string, values: Record<string, string | number>) => (
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  )
);

export default function AppCarousel({ apps, language, text, autoPlayPaused, onSelect }: AppCarouselProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);

  // Auto-play interval configuration (5 seconds)
  const AUTO_PLAY_TIME = 5000;

  // Safe Index Reset when apps array changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [apps.length]);

  useEffect(() => {
    if (autoPlayPaused) return;

    const syncHoverState = () => {
      setIsHovered(rootRef.current?.matches(':hover') ?? false);
    };

    const animationFrameId = window.requestAnimationFrame(syncHoverState);
    const timeoutId = window.setTimeout(syncHoverState, 150);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [autoPlayPaused]);

  const isAutoPlayPaused = isHovered || autoPlayPaused || isManuallyPaused;

  // Handle auto-advancing slides with timer (paused on hover for better user experience)
  useEffect(() => {
    if (apps.length <= 1 || isAutoPlayPaused) return;

    const timer = window.setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % apps.length);
    }, AUTO_PLAY_TIME);

    return () => window.clearTimeout(timer);
  }, [apps.length, currentIndex, isAutoPlayPaused]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (apps.length === 0) return null;

  const safeCurrentIndex = Math.min(currentIndex, apps.length - 1);
  const app = apps[safeCurrentIndex];
  const primaryType = app.types[0] ?? '';
  const firstScreenshot = app.screenshots[0];

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % apps.length);
  };

  const handlePrev = () => {
    setCurrentIndex(prev => (prev - 1 + apps.length) % apps.length);
  };

  // Helper for picking icons based on App category/type
  const getAppTypeIcon = (type: string) => {
    if (app.icon) {
      return <img src={app.icon} alt="" className="w-full h-full object-contain" />;
    }

    const typeStr = type.toLowerCase();
    if (typeStr.includes('native') || typeStr.includes('ios') || typeStr.includes('android')) {
      return <Smartphone size={48} className="text-indigo-600 animate-bounce" />;
    }
    if (typeStr.includes('web') || typeStr.includes('react')) {
      return <Code size={48} className="text-emerald-600" />;
    }
    return <Globe size={48} className="text-sky-600" />;
  };

  const typePillClass = 'bg-sky-50 text-sky-700 border-sky-200';

  return (
    <div 
      ref={rootRef}
      className="max-w-4xl mx-auto w-full transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      dir={language.isRtl ? 'rtl' : 'ltr'}
    >
      <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden flex flex-col transition-all duration-300 hover:shadow-2xl">
        
        {/* Slide Main Frame with generous padding */}
        <div className="p-8 sm:p-12 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center min-h-[440px] pt-12 relative">
          
          {/* Left Column: App Specifications and descriptions */}
          <div className="md:col-span-7 flex flex-col justify-between h-full space-y-8 z-10">
            <div className="space-y-6">
              
              {/* Badges & Category Type */}
              <div className="flex flex-wrap items-center gap-2">
                {app.types.map(type => (
                  <span key={type} dir="ltr" className={`inline-flex px-3.5 py-1.5 rounded-full text-xs font-extrabold border shadow-2xs tracking-wide ${typePillClass}`}>
                    {type}
                  </span>
                ))}
              </div>

              {/* Majestic App Title */}
              <div className="flex items-center gap-3 min-w-0">
                <span className="hidden min-[520px]:flex w-12 h-12 items-center justify-center shrink-0">
                  {getAppTypeIcon(primaryType)}
                </span>
                <h3 className="min-w-0 truncate text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight font-display">
                  {app.name}
                </h3>
              </div>

              {/* Description */}
              <p
                dir={language.isRtl ? 'rtl' : 'ltr'}
                className={`text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl whitespace-pre-line font-sans [unicode-bidi:plaintext] ${language.isRtl ? 'text-right' : 'text-left'}`}
              >
                {app.description}
              </p>
            </div>

            {/* App actions */}
            <div className="flex flex-wrap gap-3.5 pt-6 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => onSelect(app)}
                className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 text-xs sm:text-sm font-bold px-4 sm:px-5 py-3 rounded-2xl transition-all cursor-pointer hover:border-slate-300 dark:hover:border-slate-600"
              >
                <Eye size={16} />
                <span>{text.carousel_details_action}</span>
              </button>
            </div>
          </div>

          {/* Right Column: Centered device mockup with screenshots */}
          <div className="md:col-span-5 flex items-center justify-center z-10">
            <div className="w-full max-w-[280px] sm:max-w-[300px] bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[36px] p-3.5 shadow-md relative group/device aspect-[9/16] flex flex-col justify-between overflow-hidden hover:border-indigo-200 dark:hover:border-indigo-800 transition-all">
              {/* Top phone notch mockup */}
              <div className="w-24 h-4 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-2.5 shrink-0 flex items-center justify-center">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-400 dark:bg-slate-600 block" />
              </div>

              {/* First screenshot preview inside mobile device mockup frame */}
              <div className="flex-1 bg-white dark:bg-slate-950 border border-slate-150 dark:border-slate-850 rounded-[24px] relative overflow-hidden flex items-center justify-center">
                {firstScreenshot ? (
                  <div className="w-full h-full relative">
                    <img 
                      src={getScreenshotSrc(firstScreenshot)} 
                      alt={`${app.name} ${formatText(text.screenshot_alt, { number: 1 })}`}
                      className="w-full h-full object-cover transition-all duration-350 animate-in fade-in"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ) : (
                  <div className="p-6 text-center space-y-3 text-slate-400 dark:text-slate-500">
                    <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-full flex items-center justify-center mx-auto text-indigo-500 dark:text-indigo-400">
                      <ImageIcon size={22} />
                    </div>
                    <h4 className="font-bold text-xs text-slate-700 dark:text-slate-300">
                      {text.no_screenshots_title}
                    </h4>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-normal">
                      {text.no_screenshots_description}
                    </p>
                  </div>
                )}
              </div>

              {/* Bottom phone home button */}
              <div className="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-805 mx-auto mt-2.5 flex items-center justify-center cursor-pointer shadow-2xs shrink-0 transition-colors" />
            </div>
          </div>

        </div>

        {/* 🎛️ Navigation Controls Bar (next/prev and dot indicators) */}
        <div className="bg-slate-50 dark:bg-slate-850 border-t border-slate-150 dark:border-slate-800 px-8 py-5 flex items-center justify-between z-10 transition-colors duration-300">
          
          {/* Previous Slide Button (RTL-friendly) */}
          <button
            onClick={handlePrev}
            className="w-11 h-11 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-600 dark:text-slate-300 rounded-full shadow-xs transition-all cursor-pointer flex items-center justify-center hover:scale-105 active:scale-95"
            title={text.carousel_prev_title}
          >
            {language.isRtl ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>

          {/* Active Navigation Dot Indicators */}
          <div className="hidden min-[520px]:flex gap-3 justify-center items-center">
            {apps.length > 1 && (
              <button
                type="button"
                onClick={() => setIsManuallyPaused(current => !current)}
                className="w-8 h-8 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-500 dark:text-slate-400 rounded-full shadow-xs transition-all cursor-pointer flex items-center justify-center hover:scale-105 active:scale-95"
                title={isManuallyPaused ? text.carousel_play_title : text.carousel_pause_title}
                aria-label={isManuallyPaused ? text.carousel_play_title : text.carousel_pause_title}
              >
                {isManuallyPaused ? <Play size={14} /> : <Pause size={14} />}
              </button>
            )}

            <span className="bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-full px-3 py-1.5 text-xs font-bold shadow-xs">
              {safeCurrentIndex + 1} / {apps.length}
            </span>

            {apps.length > 1 && (
              <div className="flex gap-2.5 justify-center items-center">
                {apps.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${
                      safeCurrentIndex === idx 
                        ? 'w-7 h-2.5 bg-indigo-600 dark:bg-indigo-500 shadow-md shadow-indigo-100 dark:shadow-none' 
                        : 'w-2.5 h-2.5 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600'
                    }`}
                    title={formatText(text.app_dot_title, { number: idx + 1 })}
                  />
                ))}
              </div>
            )}

            <div
              className={`pointer-events-none flex w-8 h-8 items-center justify-center rounded-full bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shadow-xs transition-opacity duration-200 ${isHovered ? 'opacity-35' : 'opacity-0'}`}
              aria-hidden="true"
            >
              <Hand size={14} />
            </div>
          </div>

          {/* Next Slide Button (RTL-friendly) */}
          <button
            onClick={handleNext}
            className="w-11 h-11 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-600 dark:text-slate-300 rounded-full shadow-xs transition-all cursor-pointer flex items-center justify-center hover:scale-105 active:scale-95"
            title={text.carousel_next_title}
          >
            {language.isRtl ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>

        </div>

      </div>
    </div>
  );
}
